import { z } from "zod";

/**
 * Shared DTO cho login email (US-E2.F2-0010 / @UC0002).
 * Một nguồn sự thật cho api (S01 — validate biên) và mobile (S04 — gọi + map lỗi),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * LƯU Ý: login KHÔNG enforce policy 8 ký tự (đó là của register). Login chỉ
 * yêu cầu password hiện diện (min 1) — enumeration-safe: mọi lỗi sai
 * email/password đều trả cùng message chung.
 */
export const loginEmailRequestSchema = z.object({
  // Chuẩn hoá tại biên: trim + lowercase để khớp citext @unique nhất quán
  // với register (apps/api register-email.dto.ts dùng cùng convention).
  email: z.string().trim().toLowerCase().pipe(z.string().email()),
  password: z.string().min(1),
});

export type LoginEmailRequest = z.infer<typeof loginEmailRequestSchema>;

/**
 * Response thành công của login. C3: TUYỆT ĐỐI không chứa passwordHash/secret.
 *
 * 🔴 `refreshToken` KHÔNG phải field tuỳ chọn (US-E24.F5-0354-S01 · @UC9576). Bất biến lane auth:
 * **không đường đăng nhập nào được tạo refresh token trong DB rồi không trả về client.**
 * `SessionService.issue()` LUÔN ghi `auth_sessions { refreshTokenHash, expiresAt }`; caller vứt giá
 * trị đi ⇒ người dùng chết phiên sau `ACCESS_TOKEN_TTL` (15') dù `POST /v1/auth/refresh` tồn tại, và
 * mỗi lần login đẻ một row mồ côi sống `REFRESH_TOKEN_TTL` (30 ngày).
 */
export interface LoginEmailResponse {
  token: string;
  /** Refresh token thô (opaque, chỉ hash lưu DB) — đổi lấy cặp token mới ở `POST /v1/auth/refresh`. */
  refreshToken: string;
  user: {
    id: string;
    email: string | null;
    fullName: string | null;
    avatarUrl: string | null;
    role: string | null;
  };
  emailVerified: boolean;
}

/**
 * Danh sách field của `LoginEmailResponse` — HARDCODE có chủ đích (@UC9576 AC4).
 *
 * `LoginEmailResponse` là **interface TS thuần**, KHÔNG phải zod schema ⇒ không có `.strict()` nào
 * chặn ở biên. Hằng này là chốt chặn thay thế, khoá HAI CHIỀU với interface:
 *  - runtime: `auth-login.test.ts` + int-spec so `Object.keys(response)` với danh sách này;
 *  - typecheck: `auth-login.test.ts` gán object literal vào `LoginEmailResponse` ⇒ thêm/bớt field
 *    trong interface mà quên sửa hằng ⇒ `pnpm typecheck` ĐỎ (excess/missing property).
 */
export const LOGIN_EMAIL_RESPONSE_FIELDS = [
  "token",
  "refreshToken",
  "user",
  "emailVerified",
] as const;

/**
 * Hằng message/code lỗi login dùng chung (DRY giữa api & mobile).
 * Message enumeration-safe: không tiết lộ email tồn tại hay không (AC2/AC3).
 */
export const LOGIN_INVALID_CREDENTIALS_MESSAGE = "Email hoặc mật khẩu không đúng";
export const LOGIN_LOCKED_MESSAGE = "Tài khoản bị khoá. Thử lại sau 5 phút.";

/** Machine code ổn định, gắn vào RFC7807 problem+json `code` (AC3). */
export const LOGIN_INVALID_CREDENTIALS_CODE = "INVALID_CREDENTIALS";
export const LOGIN_LOCKED_CODE = "ACCOUNT_LOCKED";


/**
 * 🔴 US-E24.F26-0381 (@UC9664) — một email thuộc NHIỀU tài khoản (QĐ PO 09/08: cho phép trùng vì
 * siết `@@unique([keyType, keyValue, userId])` khi dữ liệu đang trùng = chặn đăng nhập người đang dùng).
 *
 * Đăng nhập OTP khi đó KHÔNG được tự chọn hộ (chọn nhầm = người dùng mất nhóm mà không hiểu vì sao)
 * và cũng KHÔNG được đẻ tài khoản mới (nghĩa vụ tiền nằm ở tài khoản họ không nhìn thấy). Fail-closed
 * bằng mã lỗi ổn định; màn "chọn tài khoản sau khi verify OTP" là bước NỐI TIẾP.
 */
export const EMAIL_MULTI_ACCOUNT_CODE = "AUTH_EMAIL_MULTIPLE_ACCOUNTS";
export const EMAIL_MULTI_ACCOUNT_MESSAGE =
  "Email này đang liên kết với nhiều tài khoản. Vui lòng liên hệ hỗ trợ để chọn đúng tài khoản của bạn.";
